import React, { forwardRef, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './Token.css';

// Format seconds to MM:SS or HH:MM:SS
const formatTime = (seconds) => {
  if (typeof seconds !== 'number' || !isFinite(seconds)) return 'N/A';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

const Token = forwardRef(({
  text,
  confidence,
  start,
  end,
  onClick,
  onUndelete,
  onWordSelect,
  onDoubleClickEnterTyping,
  typingMode = false,
  clickToDeleteMode = false,
  isActive,
  isSelected,
  isBeingEdited,
  isDeleted,
  isInserted,
  isGhosted,
  highThreshold = 0.8,
  mediumThreshold = 0.5,
  draggable = false,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  segmentIndex,
  wordIndex,
  globalIndex,
  cursorPosition,
  showConfidence = true,
  alternatives = [],
  onWordReplacement,
  suggestionsMode = false,
  openAlternativesId = null,
  onAlternativesToggle,
  readOnlyAlternatives = false,
  previousWord = null,
  nextWord = null
}, ref) => {
  const tokenId = `${segmentIndex}-${wordIndex}`;
  const isOpen = openAlternativesId === tokenId;
  const dropdownRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState(null);
  const positionedRef = useRef(false);
  const touchStartTimeRef = useRef(null);
  const touchTimerRef = useRef(null);

  // Position the dropdown intelligently to avoid flashing
  useEffect(() => {
    if (!isOpen) {
      setDropdownStyle(null);
      positionedRef.current = false;
      return;
    }

    // Only calculate position once when opening
    if (positionedRef.current) return;

    // Use requestAnimationFrame to ensure DOM is fully rendered
    const positionDropdown = () => {
      if (!dropdownRef.current || !ref) return;

      const dropdown = dropdownRef.current;
      const tokenElement = (typeof ref === 'function') ? null : ref?.current;
      if (!tokenElement) return;

      // Measure dropdown dimensions while it's off-screen
      const dropdownHeight = dropdown.offsetHeight;
      const dropdownWidth = dropdown.offsetWidth;
      const tokenRect = tokenElement.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth;

      // Calculate space above and below
      const spaceBelow = windowHeight - tokenRect.bottom;
      const spaceAbove = tokenRect.top;

      // Determine if dropdown should appear above or below
      const shouldShowAbove = spaceBelow < dropdownHeight + 10 && spaceAbove > spaceBelow;

      // Calculate vertical position
      let top;
      if (shouldShowAbove) {
        top = tokenRect.top - dropdownHeight - 4;
      } else {
        top = tokenRect.bottom + 4;
      }

      // Calculate horizontal position (ensure it stays on screen)
      let left = tokenRect.left;
      if (left + dropdownWidth > windowWidth) {
        left = windowWidth - dropdownWidth - 10;
      }
      if (left < 10) {
        left = 10;
      }

      // Set the calculated position
      setDropdownStyle({
        top: `${top}px`,
        left: `${left}px`,
        bottom: 'auto'
      });
      positionedRef.current = true;
    };

    // Position after a tiny delay to ensure content is rendered
    requestAnimationFrame(() => {
      requestAnimationFrame(positionDropdown);
    });
  }, [isOpen, ref]);

  const getConfidenceClass = (conf) => {
    if (conf === null || conf === undefined) return 'inserted'; // No confidence for inserted words
    if (conf >= highThreshold) return 'high-confidence';
    if (conf >= mediumThreshold) return 'medium-confidence';
    return 'low-confidence';
  };

  const handleClick = (e) => {
    e.stopPropagation();

    console.log('[Token] handleClick - clickToDeleteMode:', clickToDeleteMode, 'typingMode:', typingMode, 'word:', text);

    // Priority 1: Undelete a deleted word
    if (isDeleted && onUndelete) {
      console.log('[Token] Undeleting word');
      onUndelete(segmentIndex, wordIndex);
      return;
    }

    // Priority 2: Handle click-to-delete mode
    if (clickToDeleteMode && onWordSelect) {
      console.log('[Token] Click-to-delete mode active, calling onWordSelect');
      onWordSelect(segmentIndex, wordIndex, 'select');
      return;
    }

    // Priority 3: Suggestions mode - show alternatives menu instead of audio seek
    // Show menu in suggestions mode regardless of whether alternatives exist
    if (suggestionsMode && !typingMode && !isInserted) {
      console.log('[Token] Suggestions mode active, toggling alternatives');
      if (onAlternativesToggle) {
        onAlternativesToggle(isOpen ? null : tokenId);
      }
      return;
    }

    // Priority 4: Handle all actions in typing mode
    if (typingMode) {
      if (onWordSelect) {
        // Calculate cursor position within the word if clicking in typing mode
        const cursorPos = calculateCursorPosition(e);
        onWordSelect(segmentIndex, wordIndex, 'select', cursorPos);
      }
      return;
    }

    // Priority 5: Default behavior in normal mode: seek audio
    // Do NOT show alternatives dropdown when clicking - only in suggestions mode
    if (onClick) {
      // Add a small epsilon to ensure the word becomes active
      onClick(start + 0.01);
    }
  };

  const handleDoubleClick = (e) => {
    e.stopPropagation();
    e.preventDefault();

    // Don't handle double-click on deleted or ghosted words
    if (isDeleted || isGhosted) return;

    // If onDoubleClickEnterTyping is provided, use it
    if (onDoubleClickEnterTyping) {
      const cursorPos = calculateCursorPosition(e);
      onDoubleClickEnterTyping(segmentIndex, wordIndex, cursorPos);
    }
  };

  // Context menu is now handled by Transcript component
  // Token just allows the event to bubble up

  const calculateCursorPosition = (e) => {
    const tokenTextElement = e.currentTarget.querySelector('.token-text');
    if (!tokenTextElement || !text) return 0;

    try {
      // Create a temporary element to measure text width
      const tempSpan = document.createElement('span');
      const computedStyle = window.getComputedStyle(tokenTextElement);
      tempSpan.style.font = computedStyle.font;
      tempSpan.style.fontSize = computedStyle.fontSize;
      tempSpan.style.fontFamily = computedStyle.fontFamily;
      tempSpan.style.fontWeight = computedStyle.fontWeight;
      tempSpan.style.letterSpacing = computedStyle.letterSpacing;
      tempSpan.style.visibility = 'hidden';
      tempSpan.style.position = 'absolute';
      tempSpan.style.whiteSpace = 'nowrap';
      tempSpan.style.top = '-9999px';
      document.body.appendChild(tempSpan);

      // Get click position relative to the token
      const tokenRect = tokenTextElement.getBoundingClientRect();
      const clickX = e.clientX - tokenRect.left;

      // Handle clicks before the start of text
      if (clickX < 0) {
        document.body.removeChild(tempSpan);
        return 0;
      }

      let bestPosition = text.length;
      let minDistance = Infinity;

      // Check each character position
      for (let i = 0; i <= text.length; i++) {
        tempSpan.textContent = text.slice(0, i);
        const textWidth = tempSpan.offsetWidth;
        const distance = Math.abs(clickX - textWidth);

        if (distance < minDistance) {
          minDistance = distance;
          bestPosition = i;
        }

        // If we've passed the click position, we can stop
        if (textWidth > clickX) {
          break;
        }
      }

      document.body.removeChild(tempSpan);
      return Math.max(0, Math.min(bestPosition, text.length));
    } catch (error) {
      console.warn('Error calculating cursor position:', error);
      return 0;
    }
  };

  const handleAlternativeSelect = (alternative) => {
    if (onWordReplacement) {
      onWordReplacement(segmentIndex, wordIndex, alternative.word);
    }
    if (onAlternativesToggle) {
      onAlternativesToggle(null);
    }
  };

  const handleDragStart = (e) => {
    if (onDragStart) {
      onDragStart(e, segmentIndex, wordIndex);
    }
  };

  const handleDragEnd = (e) => {
    if (onDragEnd) {
      onDragEnd(e);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (onDragOver) {
      onDragOver(e, segmentIndex, wordIndex);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (onDrop) {
      onDrop(e, segmentIndex, wordIndex);
    }
  };

  // Long-press support for mobile
  const handleTouchStart = (e) => {
    // Don't handle touch on deleted or ghosted words
    if (isDeleted || isGhosted) return;

    touchStartTimeRef.current = Date.now();

    // Set a timer for long press (500ms)
    touchTimerRef.current = setTimeout(() => {
      // Long press detected - enter typing mode
      if (onDoubleClickEnterTyping) {
        const touch = e.touches[0];
        const cursorPos = calculateCursorPositionFromTouch(touch);
        onDoubleClickEnterTyping(segmentIndex, wordIndex, cursorPos);
      }
    }, 500);
  };

  const handleTouchEnd = (e) => {
    // Clear the long-press timer
    if (touchTimerRef.current) {
      clearTimeout(touchTimerRef.current);
      touchTimerRef.current = null;
    }

    // If touch was very short, it's a tap (handle normally via onClick)
    // The normal click handler will be called after this
    touchStartTimeRef.current = null;
  };

  const handleTouchMove = () => {
    // If user moves finger, cancel the long-press
    if (touchTimerRef.current) {
      clearTimeout(touchTimerRef.current);
      touchTimerRef.current = null;
    }
  };

  const calculateCursorPositionFromTouch = (touch) => {
    const tokenTextElement = ref && typeof ref !== 'function' ? ref.current?.querySelector('.token-text') : null;
    if (!tokenTextElement || !text) return 0;

    try {
      const tokenRect = tokenTextElement.getBoundingClientRect();
      const touchX = touch.clientX - tokenRect.left;

      // Use similar logic as mouse cursor calculation
      const tempSpan = document.createElement('span');
      const computedStyle = window.getComputedStyle(tokenTextElement);
      tempSpan.style.font = computedStyle.font;
      tempSpan.style.fontSize = computedStyle.fontSize;
      tempSpan.style.fontFamily = computedStyle.fontFamily;
      tempSpan.style.fontWeight = computedStyle.fontWeight;
      tempSpan.style.letterSpacing = computedStyle.letterSpacing;
      tempSpan.style.visibility = 'hidden';
      tempSpan.style.position = 'absolute';
      tempSpan.style.whiteSpace = 'nowrap';
      tempSpan.style.top = '-9999px';
      document.body.appendChild(tempSpan);

      if (touchX < 0) {
        document.body.removeChild(tempSpan);
        return 0;
      }

      let bestPosition = text.length;
      let minDistance = Infinity;

      for (let i = 0; i <= text.length; i++) {
        tempSpan.textContent = text.slice(0, i);
        const textWidth = tempSpan.offsetWidth;
        const distance = Math.abs(touchX - textWidth);

        if (distance < minDistance) {
          minDistance = distance;
          bestPosition = i;
        }

        if (textWidth > touchX) {
          break;
        }
      }

      document.body.removeChild(tempSpan);
      return Math.max(0, Math.min(bestPosition, text.length));
    } catch (error) {
      console.warn('Error calculating cursor position from touch:', error);
      return 0;
    }
  };

  const confidenceClass = getConfidenceClass(confidence);
  const displayConfidence = confidence !== null && confidence !== undefined ? confidence : 0;
  const isClickableGhost = clickToDeleteMode && isGhosted; // Delete mode works independently

  // Filter alternatives: exclude current word, previous/next words, prefer >1% probability, but show at least 3
  const getFilteredAlternatives = () => {
    if (!alternatives || alternatives.length === 0) return [];

    // Normalize text for comparison (lowercase, trim)
    const normalizeWord = (word) => (word || '').toLowerCase().trim();
    const currentNormalized = normalizeWord(text);
    const prevNormalized = previousWord ? normalizeWord(previousWord) : null;
    const nextNormalized = nextWord ? normalizeWord(nextWord) : null;

    // Exclude current word, previous word, and next word
    const withoutCurrent = alternatives.filter(alt => {
      const altNormalized = normalizeWord(alt.word);
      return altNormalized !== currentNormalized &&
             altNormalized !== prevNormalized &&
             altNormalized !== nextNormalized;
    });

    // Use confidence (AI/Whisper return confidence, not probability)
    const aboveThreshold = withoutCurrent.filter(alt => (alt.confidence || alt.probability || 0) > 0.01);

    // If we have 3+ above threshold, use those; otherwise take top 3 from all
    if (aboveThreshold.length >= 3) {
      return aboveThreshold;
    } else {
      return withoutCurrent.slice(0, 3);
    }
  };

  const filteredAlternatives = getFilteredAlternatives();
  const hasAlternatives = filteredAlternatives.length > 0 && !isInserted;

  // Render dropdown in a portal at document root to avoid positioning issues
  const dropdownPortal = isOpen && !isInserted ? createPortal(
    <div
      ref={dropdownRef}
      className={`alternatives-dropdown ${dropdownStyle ? 'positioned' : ''}`}
      style={dropdownStyle || {}}
    >
      <div className="alternatives-header">
        Alternative words:
      </div>
      {hasAlternatives ? (
        filteredAlternatives
          .slice(0, 10)
          .map((alt, index) => (
          <div
            key={index}
            className={`alternative-option ${readOnlyAlternatives ? 'read-only' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              if (!readOnlyAlternatives) {
                handleAlternativeSelect(alt);
              }
            }}
            style={readOnlyAlternatives ? { cursor: 'default', opacity: 0.8 } : {}}
          >
            <span className="alternative-word">{alt.word}</span>
          </div>
        ))
      ) : (
        <div className="no-alternatives-message">
          No alternatives available for this word
        </div>
      )}
      <div
        className="alternative-close"
        onClick={(e) => {
          e.stopPropagation();
          if (onAlternativesToggle) {
            onAlternativesToggle(null);
          }
        }}
      >
        ×
      </div>
    </div>,
    document.body
  ) : null;

  const isWhitespaceOnly = text && text.trim() === '' && text.length > 0;

  return (
    <>
      <span
        ref={ref}
        className={`token ${confidenceClass} ${isActive ? 'active' : ''} ${isSelected ? 'selected' : ''} ${isBeingEdited ? 'editing' : ''} ${isDeleted ? 'deleted' : ''} ${isInserted ? 'inserted' : ''} ${isGhosted ? 'ghosted' : ''} ${isClickableGhost ? 'clickable-ghost' : ''} ${hasAlternatives ? 'has-alternatives' : ''} ${isWhitespaceOnly ? 'whitespace-only' : ''}`}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchMove={handleTouchMove}
        draggable={draggable}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        data-global-index={globalIndex}
        data-segment-index={segmentIndex}
        data-word-index={wordIndex}
        title={`${isInserted ? 'Inserted word' : `Confidence: ${Math.min((displayConfidence * 100), 99).toFixed(1)}%`} | ${formatTime(start)} - ${formatTime(end)}${isDeleted ? ' (DELETED - Click to restore)' : clickToDeleteMode ? ' (Click to delete)' : hasAlternatives ? ' (Click for suggestions)' : ''}`}
      >
      <div className="token-text">
        {isSelected && cursorPosition !== null ? (
          <>
            {text.slice(0, cursorPosition)}
            <span className="editing-cursor"></span>
            {text.slice(cursorPosition)}
          </>
        ) : (
          text
        )}
        {(hasAlternatives || suggestionsMode) && !isDeleted && !isInserted && (
          <span className="suggestion-indicator" title="Click to see suggestions">⌄</span>
        )}
      </div>
      {showConfidence && (
        <div className="token-confidence">
          {isInserted ? 'NEW' : `${Math.min((displayConfidence * 100), 99).toFixed(0)}%`}
        </div>
      )}
    </span>
    {dropdownPortal}
  </>
  );
});

Token.displayName = 'Token';

export default React.memo(Token);